import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Alert, Button, Card, Input, Tag } from '@rallyshift/design-system'
import api, { setToken } from '../api/client'
import { isTenantHost, setOrgSlugOverride } from '../api/tenant'
import { useAuth } from '../contexts/AuthContext'
import { useOrganization } from '../contexts/OrganizationContext'

type Invitation = Awaited<ReturnType<typeof api.invitations.lookup>>

/**
 * The landing page for an invitation link. The organisation comes from the token
 * rather than the host, so a link still shows the right branding when a volunteer
 * opens it from a mail client that rewrites URLs.
 */
const AcceptInvitationPage: React.FC = () => {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { refreshProfile } = useAuth()
  const { refresh: refreshOrganization } = useOrganization()

  const token = params.get('token') ?? ''

  const [invitation, setInvitation] = useState<Invitation | null>(null)
  const [loading, setLoading] = useState(true)
  const [blocked, setBlocked] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [skills, setSkills] = useState<string[]>([])
  const [catalogue, setCatalogue] = useState<string[]>([])

  const load = useCallback(async () => {
    if (!token) {
      setBlocked('Deze link is niet compleet. Open de link uit je e-mail nog een keer.')
      setLoading(false)
      return
    }
    try {
      const found = await api.invitations.lookup(token)
      setInvitation(found)
      setFirstName(found.first_name ?? '')
      setLastName(found.last_name ?? '')
      setSkills(found.skills ?? [])
      setCatalogue(found.skill_catalogue ?? [])
    } catch (e: any) {
      setBlocked(e.message || 'Deze uitnodiging werkt niet meer.')
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    load()
  }, [load])

  const accept = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!invitation) return

    if (invitation.needs_account) {
      if (password.length < 6) {
        setError('Kies een wachtwoord van minstens 6 tekens.')
        return
      }
      if (password !== confirmPassword) {
        setError('De twee wachtwoorden zijn niet gelijk.')
        return
      }
    }

    setSubmitting(true)
    setError('')
    try {
      const result = await api.invitations.accept({
        token,
        password: invitation.needs_account ? password : undefined,
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        phone_number: phoneNumber.trim() || undefined,
        skills,
      })
      setToken(result.token)

      // An invitation can be opened on any host, so the tab may still be pointed at
      // the wrong organisation — or none. Pin it to the one they just joined before
      // the dashboard asks the backend for anything.
      if (!isTenantHost()) setOrgSlugOverride(result.organization.slug)

      await refreshOrganization()
      await refreshProfile()
      navigate('/dashboard', { replace: true })
    } catch (e: any) {
      setError(e.message || 'Accepteren is niet gelukt.')
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-page">
        <div className="h-8 w-8 animate-rs-spin rounded-full border-2.5 border-tenant-brand border-r-transparent" />
      </div>
    )
  }

  if (blocked || !invitation) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-page px-4">
        <div className="w-full max-w-md">
          <Card>
            <h1 className="font-display text-display-sm text-text-primary">
              Deze uitnodiging werkt niet meer
            </h1>
            <p className="mt-2 text-md text-text-secondary">{blocked}</p>
            <p className="mt-4 text-sm text-text-muted">
              Vraag de organisator om een nieuwe uitnodiging, of log in als je al een
              account hebt.
            </p>
            <div className="mt-6">
              <Button variant="secondary" fullWidth onClick={() => navigate('/login')}>
                Naar inloggen
              </Button>
            </div>
          </Card>
        </div>
      </div>
    )
  }

  const org = invitation.organization

  return (
    <div className="min-h-screen bg-surface-page">
      <header
        className="flex items-center justify-center px-4 py-8"
        style={{ background: org.brand_color }}
      >
        {org.logo_url ? (
          <img src={org.logo_url} alt={org.name} className="h-14" />
        ) : (
          <span
            className="font-display text-display-md"
            style={{ color: org.on_brand_color }}
          >
            {org.name}
          </span>
        )}
      </header>

      <div className="mx-auto w-full max-w-md px-4 py-8">
        <h1 className="font-display text-display-md text-text-primary">
          {firstName ? `Doe je mee, ${firstName}?` : 'Doe je mee?'}
        </h1>
        <p className="mt-2 text-md text-text-secondary">
          {org.name} nodigt je uit om vrijwilliger te worden. Je kiest daarna zelf de
          diensten die je passen.
        </p>

        {error && (
          <div className="mt-4">
            <Alert variant="danger" onDismiss={() => setError('')}>
              {error}
            </Alert>
          </div>
        )}

        <form onSubmit={accept} className="mt-6">
          <Card>
            <div className="space-y-5">
              <Input label="E-mailadres" value={invitation.email} disabled size="lg" />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  label="Voornaam"
                  size="lg"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                <Input
                  label="Achternaam"
                  size="lg"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>

              <Input
                label="Telefoonnummer"
                hint="Zodat we je op de dag zelf kunnen bereiken."
                size="lg"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />

              {invitation.needs_account && (
                <>
                  <Input
                    label="Kies een wachtwoord"
                    size="lg"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <Input
                    label="Herhaal je wachtwoord"
                    size="lg"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </>
              )}

              {!invitation.needs_account && (
                <Alert variant="info">
                  Je hebt al een RallyShift-account. Je houdt je huidige wachtwoord.
                </Alert>
              )}

              {catalogue.length > 0 && (
                <div>
                  <p className="mb-1 text-sm font-semibold text-text-primary">
                    Wat kun je goed? (optioneel)
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {catalogue.map((skill) => {
                      const chosen = skills.includes(skill)
                      return (
                        <Tag
                          key={skill}
                          selected={chosen}
                          onClick={() =>
                            setSkills((prev) =>
                              chosen ? prev.filter((s) => s !== skill) : [...prev, skill],
                            )
                          }
                        >
                          {chosen ? `\u2713 ${skill}` : skill}
                        </Tag>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6">
              <Button type="submit" variant="tenant" size="lg" fullWidth loading={submitting}>
                Uitnodiging accepteren
              </Button>
            </div>
          </Card>
        </form>

        <p className="mt-6 text-center text-xs text-text-muted">
          mogelijk gemaakt door <span className="font-bold text-action-primary">RallyShift</span>
        </p>
      </div>
    </div>
  )
}

export default AcceptInvitationPage
